import React from 'react';
import { MonthlyDataPoint } from '../../types/dashboard';
import { PROVIDER_COLORS } from '../../utils/providerColors';
import { CloudProvider } from '../../contexts/ProviderContext';
import { CurrencyCode, getCurrencySymbol } from '../../utils/currency';

/** Narrow an arbitrary currency string to a known CurrencyCode, defaulting to USD */
function toCurrencyCode(currency: string): CurrencyCode {
  return (['USD', 'INR', 'EUR', 'GBP'] as const).includes(currency as CurrencyCode)
    ? (currency as CurrencyCode)
    : 'USD';
}

/**
 * SpendChart component for visualizing monthly spend trends.
 * Supports two rendering modes:
 *   - Line chart: single-provider line using the provider's brand color
 *     (when a specific provider is selected) [REQ-012, Task 34]
 *   - Stacked bars: colored by provider (when "All Providers" is selected)
 *
 * REQ-012-HYBRID: Stacked bars show cost over time with each bar segment
 * representing a provider's contribution using the established color scheme:
 *   AWS = #FF9900, Azure = #0089D6, GCP = #34A853
 *
 * REQ-012: When a provider filter is active, the chart switches to a line chart
 * rendered with the selected provider's brand color, displaying filtered time
 * series data for just that one provider.
 *
 * WCAG 2.1 AA compliant with accessible data table fallback.
 */

/** Provider breakdown used to calculate stacked bar segments */
export interface ProviderCostBreakdown {
  provider: string;
  cost: number;
  percentage: number;
}

interface SpendChartProps {
  data: MonthlyDataPoint[];
  title: string;
  showBudget?: boolean;
  /** Current provider filter — determines stacked vs single bar mode */
  activeProvider?: CloudProvider;
  /** Per-provider cost breakdown (from costByProvider) — drives stacked proportions */
  providerBreakdown?: ProviderCostBreakdown[];
  /** Currency to display amounts in (native currency for single-provider views, USD for All Providers) */
  currency?: string;
  /**
   * How many accounts the budget line covers, and how many are spending.
   *
   * The bars are TOTAL spend, but budgets are set per account and a tenant is
   * rarely fully budgeted - so a line drawn from the sum of two accounts'
   * budgets sits under bars representing nine accounts' spend, and looks like
   * a breach that has not happened. Naming the coverage in the legend is what
   * keeps the line readable instead of alarming.
   */
  budgetCoverage?: { budgeted: number; total: number };
}

/** Ordered list of providers for consistent stacking (bottom to top) */
const STACKED_PROVIDERS: Array<{ key: string; color: string; label: string }> = [
  { key: 'aws', color: PROVIDER_COLORS.aws, label: 'AWS' },
  { key: 'azure', color: PROVIDER_COLORS.azure, label: 'Azure' },
  { key: 'gcp', color: PROVIDER_COLORS.gcp, label: 'GCP' },
];

export const SpendChart: React.FC<SpendChartProps> = ({
  data,
  title,
  showBudget = true,
  activeProvider = 'all',
  providerBreakdown,
  currency = 'USD',
  budgetCoverage,
}) => {
  // Determine whether to render stacked bars
  const isStacked = activeProvider === 'all' && providerBreakdown && providerBreakdown.length > 0;

  // Determine whether to render a single-provider line chart (REQ-012, Task 34)
  const isSingleProviderLine = activeProvider !== 'all';

  // Get the active provider's brand color for the line chart
  const providerLineColor = React.useMemo(() => {
    if (!isSingleProviderLine) return '#3B82F6'; // fallback blue
    const key = activeProvider as keyof typeof PROVIDER_COLORS;
    return PROVIDER_COLORS[key] || '#3B82F6';
  }, [activeProvider, isSingleProviderLine]);

  // Provider display name for legend/labels
  const providerLabel = React.useMemo(() => {
    const labels: Record<string, string> = { aws: 'AWS', azure: 'Azure', gcp: 'GCP' };
    return labels[activeProvider] || activeProvider.toUpperCase();
  }, [activeProvider]);

  // Per-bar provider shares for stacking, computed from THAT bar's own real
  // by_provider split (see MonthlyDataPoint.by_provider) rather than a single
  // ratio derived from the whole period's total and replayed on every bar -
  // the old approach meant every month showed an identical provider mix even
  // when e.g. a new GCP account was onboarded mid-period or AWS spend spiked
  // in just one month. Falls back to the period-total ratio only for a point
  // that genuinely has no by_provider data of its own.
  const periodShares = React.useMemo(() => {
    if (!isStacked || !providerBreakdown) return {};
    const totalCost = providerBreakdown.reduce((sum, p) => sum + p.cost, 0);
    if (totalCost === 0) return {};
    const shares: Record<string, number> = {};
    for (const p of providerBreakdown) {
      shares[p.provider.toLowerCase()] = p.cost / totalCost;
    }
    return shares;
  }, [isStacked, providerBreakdown]);

  const getPointProviderShares = React.useCallback(
    (point: MonthlyDataPoint): Record<string, number> => {
      const byProvider = point.by_provider;
      if (byProvider && Object.keys(byProvider).length > 0) {
        const pointTotal = Object.values(byProvider).reduce((sum, c) => sum + c, 0);
        if (pointTotal > 0) {
          const shares: Record<string, number> = {};
          for (const [key, cost] of Object.entries(byProvider)) {
            shares[key.toLowerCase()] = cost / pointTotal;
          }
          return shares;
        }
      }
      return periodShares;
    },
    [periodShares]
  );

  // Which providers actually appear in ANY point - drives which legend
  // swatches show. Using per-point shares (not just the period total) means
  // a provider that only had spend in some months still gets a legend entry.
  const presentProviderKeys = React.useMemo(() => {
    if (!isStacked) return new Set<string>();
    const keys = new Set<string>();
    for (const point of data) {
      for (const [key, share] of Object.entries(getPointProviderShares(point))) {
        if (share > 0) keys.add(key);
      }
    }
    return keys;
  }, [isStacked, data, getPointProviderShares]);

  // Always start Y-axis from 0 for proper proportional display
  const maxValue = Math.max(...data.map((d) => Math.max(d.spend, d.budget || 0))) * 1.15;

  /**
   * The single budget figure shared by every bucket, or null if they differ.
   *
   * Budgets are per calendar month and the current bucket's line is pro-rated
   * to how much of it has elapsed, so a chart spanning several months - or one
   * whose newest bar is mid-month - legitimately has a different budget per
   * bar. One flat line cannot express that, so it is drawn only when the
   * buckets genuinely agree, and per-bar markers are used otherwise.
   */
  const budgetValues = data.map((d) => d.budget).filter((b): b is number => typeof b === 'number' && b > 0);
  const uniformBudget =
    budgetValues.length === data.length && new Set(budgetValues).size === 1 ? budgetValues[0] : null;
  const showPerBarBudget = showBudget && uniformBudget == null && budgetValues.length > 0;
  const chartHeight = 200;
  const yAxisSteps = 5;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: toCurrencyCode(currency),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatShortCurrency = (value: number) => {
    const symbol = getCurrencySymbol(toCurrencyCode(currency));
    if (value >= 1000000) return `${symbol}${(value / 1000000).toFixed(1)}M`;
    // One decimal below $100K. With toFixed(0) everywhere, an axis running
    // 0 / 1.5K / 3K / 4.5K rendered as 0 / 2K / 3K / 5K - unevenly spaced
    // labels against evenly spaced gridlines, which reads as broken even
    // though the bars are right.
    if (value >= 100000) return `${symbol}${(value / 1000).toFixed(0)}K`;
    if (value >= 1000) return `${symbol}${(value / 1000).toFixed(1)}K`;
    return `${symbol}${Math.round(value)}`;
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6 shadow-sm dark:border-border dark:bg-card">
      <h3 className="mb-4 text-lg font-semibold text-foreground">{title}</h3>

      {/* Visual Chart */}
      <div className="mb-4" role="img" aria-label={`${title} ${isStacked ? 'stacked bar' : 'bar'} chart`}>
        <div className="flex">
          {/* Y-axis labels
              Positioned ABSOLUTELY at each step's exact percentage and pulled up
              half their own height, so every label sits ON the gridline it
              describes. Previously this was a flex column with justify-between,
              which aligns the first label's TOP edge and the last label's BOTTOM
              edge to the chart bounds - so each label drifted from its gridline
              by half a line-height, worst at the top and bottom. */}
          <div className="relative min-w-[3rem] pr-2" style={{ height: `${chartHeight}px` }}>
            {Array.from({ length: yAxisSteps + 1 }, (_, i) => {
              const value = maxValue - (maxValue / yAxisSteps) * i;
              return (
                <span
                  key={i}
                  className="absolute right-2 -translate-y-1/2 text-xs text-muted-foreground whitespace-nowrap"
                  style={{ top: `${(i / yAxisSteps) * 100}%` }}
                >
                  {formatShortCurrency(Math.round(value))}
                </span>
              );
            })}
          </div>

          {/* Chart area */}
          <div className="flex-1 relative border-l border-b border-border">
            {/* Grid lines - one per label, including the topmost. The old loop
                ran 1..yAxisSteps, so it drew no line at the maximum value while
                a label for it existed, leaving the top label pointing at empty
                space. */}
            {Array.from({ length: yAxisSteps + 1 }, (_, i) => (
              <div
                key={i}
                className="absolute w-full border-t border-border dark:border-border"
                style={{ bottom: `${(i / yAxisSteps) * 100}%` }}
              />
            ))}

            {/* Budget line.
                Drawn as one flat line ONLY when every bucket shares the same
                budget. It used to take data[0].budget unconditionally and
                stretch it across the whole chart, which silently threw away
                every other bucket's figure - so a budget that changed between
                months, or the pro-rated figure on the current incomplete
                bucket, could never be seen. When the buckets differ, each bar
                gets its own marker below instead. */}
            {showBudget && uniformBudget != null && (
              <div
                className="absolute w-full border-t-2 border-dashed border-border dark:border-border z-10"
                style={{ bottom: `${(uniformBudget / maxValue) * 100}%` }}
                aria-hidden="true"
              />
            )}

            {/* ─── Bar Chart Mode (Stacked, single-provider, or Standard) ─── */}
            <div className="flex items-end justify-around h-full px-2 gap-3" style={{ height: `${chartHeight}px` }}>
              {data.map((point, index) => {
                const spendHeight = (point.spend / maxValue) * chartHeight;
                const pointShares = isStacked ? getPointProviderShares(point) : {};

                return (
                  <div key={index} className="flex flex-1 flex-col items-center">
                    <div className="relative w-full flex justify-center" style={{ height: `${chartHeight}px`, alignItems: 'flex-end' }}>
                      {/* This bucket's own budget, when buckets differ. Sits at
                          the bar's own height rather than a chart-wide line, so
                          a month whose budget was raised, or a part-finished
                          month whose line is pro-rated, reads correctly instead
                          of being compared against some other bucket's number. */}
                      {showPerBarBudget && point.budget ? (
                        <div
                          className="absolute left-0 right-0 border-t-2 border-dashed border-foreground/40 z-10"
                          style={{ bottom: `${(point.budget / maxValue) * chartHeight}px` }}
                          title={`Budget for ${point.month}: ${formatCurrency(point.budget)}`}
                          aria-hidden="true"
                        />
                      ) : null}
                      {isStacked ? (
                        /* Stacked bars — REQ-012-HYBRID: colored by provider */
                        <div
                          className="w-3/4 max-w-16 flex flex-col-reverse rounded-t overflow-hidden"
                          style={{ height: `${spendHeight}px` }}
                          title={`${point.month}: ${formatCurrency(point.spend)}`}
                          aria-label={`${point.month}: Total ${formatCurrency(point.spend)} — ${STACKED_PROVIDERS.map(
                            (p) => `${p.label}: ${formatCurrency(point.spend * (pointShares[p.key] || 0))}`
                          ).join(', ')}${point.budget ? `, Budget ${formatCurrency(point.budget)}` : ''}`}
                        >
                          {STACKED_PROVIDERS.map((p) => {
                            const share = pointShares[p.key] || 0;
                            if (share === 0) return null;
                            return (
                              <div
                                key={p.key}
                                className="w-full transition-all hover:opacity-80"
                                style={{
                                  height: `${share * 100}%`,
                                  backgroundColor: p.color,
                                }}
                                title={`${p.label}: ${formatCurrency(point.spend * share)}`}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        /* Single-color bar — provider's brand color when a single
                           provider is active (REQ-012), otherwise standard blue */
                        <div
                          className="w-3/4 max-w-16 rounded-t transition-all hover:opacity-80"
                          style={{
                            height: `${spendHeight}px`,
                            backgroundColor: isSingleProviderLine ? providerLineColor : '#3B82F6',
                          }}
                          title={`${point.month}: ${formatCurrency(point.spend)}`}
                          aria-label={`${point.month}: ${isSingleProviderLine ? `${providerLabel} Spend` : 'Spend'} ${formatCurrency(point.spend)}${
                            point.budget ? `, Budget ${formatCurrency(point.budget)}` : ''
                          }`}
                        />
                      )}
                    </div>
                    <span className="mt-2 text-xs text-muted-foreground">
                      {point.month.split(' ')[0]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-sm mt-2 flex-wrap">
        {isStacked ? (
          /* Provider-colored legend for stacked mode */
          <>
            {STACKED_PROVIDERS.map((p) => {
              if (!presentProviderKeys.has(p.key)) return null;
              return (
                <div key={p.key} className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded"
                    style={{ backgroundColor: p.color }}
                    aria-hidden="true"
                  />
                  <span className="text-muted-foreground">{p.label}</span>
                </div>
              );
            })}
          </>
        ) : isSingleProviderLine ? (
          /* Provider-branded legend for single-provider bar chart (REQ-012) */
          <div className="flex items-center gap-2">
            <div
              className="h-3 w-3 rounded"
              style={{ backgroundColor: providerLineColor }}
              aria-hidden="true"
            />
            <span className="text-muted-foreground">{providerLabel} Spend</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-primary dark:bg-primary" aria-hidden="true" />
            <span className="text-muted-foreground">Actual Spend</span>
          </div>
        )}
        {showBudget && (
          <div className="flex items-center gap-2">
            <div className="h-0.5 w-4 border-t-2 border-dashed border-border dark:border-border" aria-hidden="true" />
            {/* "per bar", because the line is the monthly budget scaled to
                whatever each bar represents — a day, a week or a month. Without
                saying so, a viewer reads the dashed line as the full monthly
                budget and concludes spend is a fraction of it. */}
            <span className="text-muted-foreground">
              Budget (per bar)
              {budgetCoverage && budgetCoverage.budgeted < budgetCoverage.total
                ? ` · covers ${budgetCoverage.budgeted} of ${budgetCoverage.total} accounts`
                : ''}
            </span>
          </div>
        )}
      </div>

      {/* Accessible data table (visually hidden but available to screen readers) */}
      <table className="sr-only" aria-label={`${title} data table`}>
        <thead>
          <tr>
            <th>Month</th>
            <th>{isSingleProviderLine ? `${providerLabel} Spend` : 'Spend'}</th>
            {isStacked && STACKED_PROVIDERS.map((p) => (
              <th key={p.key}>{p.label}</th>
            ))}
            {showBudget && <th>Budget</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((point, index) => {
            const pointShares = isStacked ? getPointProviderShares(point) : {};
            return (
              <tr key={index}>
                <td>{point.month}</td>
                <td>{formatCurrency(point.spend)}</td>
                {isStacked && STACKED_PROVIDERS.map((p) => (
                  <td key={p.key}>{formatCurrency(point.spend * (pointShares[p.key] || 0))}</td>
                ))}
                {showBudget && <td>{point.budget ? formatCurrency(point.budget) : 'N/A'}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
